/* ============================================================
   FLIPSI CANONICAL JS — S4-R component registry.
   Rules (approved 12 Jul 2026):
   - every interaction registers as a component bound via [data-flipsi]
   - init runs on first load AND on shopify:section:load (editor re-renders)
   - idempotent (data-fs-bound guard), multi-instance-safe (root-scoped queries)
   - keyboard support where relevant; honours prefers-reduced-motion
   - behaviour ported 1:1 from the approved source previews
   ============================================================ */
(function () {
  'use strict';
  var REG = {};
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var EDITOR = !!(window.Shopify && window.Shopify.designMode);

  function register(name, init) { REG[name] = init; }

  function initIn(scope) {
    scope = scope || document;
    var roots = Array.prototype.slice.call(scope.querySelectorAll('[data-flipsi]'));
    if (scope.matches && scope.matches('[data-flipsi]')) roots.unshift(scope);
    for (var i = 0; i < roots.length; i++) {
      var el = roots[i];
      var names = el.dataset.flipsi.split(/\s+/);
      for (var n = 0; n < names.length; n++) {
        var name = names[n];
        if (!REG[name]) continue;
        var guard = 'fsBound' + name.replace(/-([a-z])/g, function (m, c) { return c.toUpperCase(); });
        if (el.dataset[guard]) continue;
        el.dataset[guard] = '1';
        try { REG[name](el); } catch (e) { if (window.console) console.error('[flipsi]', name, e); }
      }
    }
  }

  function data(root) {
    var s = root.querySelector('script[data-fs-data]');
    if (!s) return null;
    try { return JSON.parse(s.textContent); } catch (e) { return null; }
  }

  /* ---------- image fallback (source: imgFail) ---------- */
  window.imgFail = function (img) {
    var w = img.closest('[data-ph]') || img.parentElement;
    img.style.display = 'none';
    if (w) w.classList.add('ph-on');
  };

  /* ---------- flip-card: spoiler-safe tap/keyboard flip ---------- */
  register('flip-card', function (fc) {
    function flipToggle() {
      var f = fc.classList.toggle('is-flipped');
      if (f) { fc.classList.remove('did-reveal'); void fc.offsetWidth; fc.classList.add('did-reveal'); }
    }
    fc.addEventListener('click', flipToggle);
    fc.addEventListener('keydown', function (e) {
      if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); flipToggle(); }
    });
  });

  /* ---------- video-stage: muted loop, play/pause, reduced-motion ---------- */
  register('video-stage', function (root) {
    var v = root.querySelector('video');
    var c = root.querySelector('[data-fs-videoctrl]');
    if (!v || !c) return;
    function set() { c.textContent = v.paused ? 'Play' : 'Pause'; c.setAttribute('aria-pressed', String(!v.paused)); }
    if (!REDUCED) { v.autoplay = true; v.play().catch(function () {}); }
    set();
    c.addEventListener('click', function () { if (v.paused) { v.play(); } else { v.pause(); } set(); });
    v.addEventListener('play', set); v.addEventListener('pause', set);
  });

  /* ---------- rail: arrow-scrolled row ---------- */
  register('rail', function (root) {
    var rail = root.querySelector('[data-fs-rail]');
    if (!rail) return;
    var step = parseInt(root.dataset.fsRailStep || '300', 10);
    var prev = root.querySelector('[data-fs-rail-prev]');
    var next = root.querySelector('[data-fs-rail-next]');
    if (prev) prev.addEventListener('click', function () { rail.scrollBy({ left: -step, behavior: 'smooth' }); });
    if (next) next.addEventListener('click', function () { rail.scrollBy({ left: step, behavior: 'smooth' }); });
  });

  /* ---------- chooser-trigger: DIRECT binding on the opener element ----------
     The editor preview demonstrably delivers clicks to element-bound handlers
     (flip hero, artwork toggle); document-level delegation proved unreliable
     there. Direct binding is now the primary open path. */
  register('chooser-trigger', function (root) {
    var triggers = root.querySelectorAll('[data-fs-open]');
    for (var i = 0; i < triggers.length; i++) {
      (function (btn) {
        if (btn.dataset.fsTriggerBound) return;
        btn.dataset.fsTriggerBound = '1';
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          if (window.__fsChooserOpen) { window.__fsChooserOpen(); return; }
          var roots = document.querySelectorAll('[data-flipsi~="chooser"]');
          for (var k = 0; k < roots.length; k++) {
            if (roots[k].__fsChooserOpen) { roots[k].__fsChooserOpen(); return; }
          }
          if (window.console) console.warn('[flipsi] chooser trigger: no initialized chooser in document');
        });
      })(triggers[i]);
    }
  });

  /* ---------- art-toggle: Shop All 'Show the artwork' (approved) ----------
     Flips only cards carrying data-flip="1" (box + front-artwork both present).
     Sets/Plus/accessories/Twin never carry data-flip. Spoiler-safe: the art
     face is the known FRONT artwork only. Idempotent; editor-re-render safe;
     reduced-motion gets an instant, transition-free swap (CSS). */
  register('art-toggle', function (root) {
    var btn = root.querySelector('[data-fs-artbtn]');
    if (!btn) return;
    var off = btn.dataset.labelOff || 'Show the artwork';
    var on = btn.dataset.labelOn || 'Show the boxes';
    function apply(state) {
      root.classList.toggle('show-art', state);
      btn.classList.toggle('is-on', state);
      btn.setAttribute('aria-pressed', state ? 'true' : 'false');
      btn.textContent = state ? on : off;
    }
    apply(root.classList.contains('show-art'));
    btn.addEventListener('click', function () {
      apply(!root.classList.contains('show-art'));
    });
  });

  /* ---------- glow: seal-spotlight follows pointer (act3) ---------- */
  register('glow', function (root) {
    var glow = root.querySelector('[data-fs-glow]');
    if (!glow) return;
    root.addEventListener('mousemove', function (e) {
      var r = root.getBoundingClientRect();
      glow.style.transform = 'translate(' + (e.clientX - r.left - 280) + 'px,' + (e.clientY - r.top - 280) + 'px)';
    });
  });

  /* ---------- reveal: gentle scroll fades (.fade -> .in) ---------- */
  register('reveal', function (root) {
    var els = root.querySelectorAll('.fade');
    if (!els.length) return;
    if (EDITOR || REDUCED || !('IntersectionObserver' in window)) {
      for (var i = 0; i < els.length; i++) els[i].classList.add('in');
      return;
    }
    var io = new IntersectionObserver(function (es) {
      es.forEach(function (e) { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } });
    }, { threshold: .15 });
    for (var j = 0; j < els.length; j++) io.observe(els[j]);
  });

  /* ---------- qty stepper ---------- */
  register('qty', function (root) {
    var input = root.querySelector('input');
    root.querySelectorAll('button[data-fs-qty]').forEach(function (b) {
      b.addEventListener('click', function () {
        input.value = Math.max(1, parseInt(input.value, 10) + parseInt(b.dataset.fsQty, 10));
      });
    });
  });

  /* ---------- gallery: thumbs swap the stage (image or video-first) ---------- */
  register('gallery', function (root) {
    var stageImg = root.querySelector('[data-fs-stage-img]');
    var vwrap = root.querySelector('[data-fs-vwrap]');
    var imgstage = root.querySelector('[data-fs-imgstage]');
    var vid = root.querySelector('video');
    root.querySelectorAll('.thumb').forEach(function (t) {
      t.addEventListener('click', function () {
        root.querySelectorAll('.thumb').forEach(function (x) { x.classList.remove('is-active'); });
        t.classList.add('is-active');
        if (vwrap && imgstage) { /* board: video <-> image switching */
          var isVideo = t.dataset.media === 'video';
          vwrap.classList.toggle('is-off', !isVideo);
          imgstage.classList.toggle('is-on', !isVideo);
          if (isVideo) { if (vid && !REDUCED) vid.play().catch(function () {}); }
          else { if (vid) vid.pause(); if (stageImg && t.dataset.src) stageImg.src = t.dataset.src; }
        } else if (stageImg && t.dataset.src) {
          stageImg.src = t.dataset.src;
        }
      });
    });
  });

  /* ---------- state-controller: availability state -> body classes + CTA labels ---------- */
  register('state-controller', function (root) {
    var state = root.dataset.fsState || 'in_stock';
    document.body.classList.remove('fs-state-reserve', 'fs-state-soldout');
    if (state === 'reserve') document.body.classList.add('fs-state-reserve');
    if (state === 'sold_out' || state === 'retired') document.body.classList.add('fs-state-soldout');
    function syncCtas() {
      var so = document.body.classList.contains('fs-state-soldout');
      var pre = document.body.classList.contains('fs-state-reserve');
      document.querySelectorAll('[data-cta]').forEach(function (el) {
        var t = so ? el.dataset.soldout : (pre ? el.dataset.reserve : el.dataset.instock);
        if (t) el.textContent = t;
      });
    }
    syncCtas();
  });

  /* ---------- sticky-atc: mobile bar appears past the buy row ---------- */
  register('sticky-atc', function (root) {
    var bar = document.querySelector('[data-fs-sticky-bar]');
    var row = root.querySelector('.buy-row') || root;
    if (!bar || !('IntersectionObserver' in window)) return;
    new IntersectionObserver(function (en) {
      var e = en[en.length - 1];   // latest entry (batched callbacks after re-renders)
      bar.classList.toggle('is-on', !e.isIntersecting && e.boundingClientRect.top < 0);
    }, { threshold: 0 }).observe(row);
  });

  /* ---------- Klaviyo client API (wired 15 Aug 2026) ----------
     Shared by the notify and joinlist components. company_id is the
     account's public API key, checked against the live Klaviyo account the
     day this landed. These client endpoints answer CORS requests, so unlike
     the old no-cors path, success and failure here are real, not assumed. */
  var KLAVIYO_COMPANY = 'Tiwiag';
  function klaviyoPost(path, payload) {
    return fetch('https://a.klaviyo.com/client/' + path + '/?company_id=' + KLAVIYO_COMPANY, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
      body: JSON.stringify(payload)
    }).then(function (res) {
      if (!res.ok) { throw new Error('klaviyo ' + path + ' ' + res.status); }
      return res;
    });
  }
  function emailOk(email) { return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email); }

  /* ---------- fsLead: tell the ad platforms a lead happened (wired 2 Sep 2026) ----------
     Every call is guarded: a blocker that removed fbq/gtag/clarity makes this a
     no-op, never an error. Category is the capture ('hold', 'email'…), name is
     what was held. Reads the theme kill-switch the HP3 page also honours. */
  function fsLead(category, name) {
    if (window.HP3_TRACK === false) return;
    try { if (typeof window.fbq === 'function') window.fbq('track', 'Lead', { content_name: name, content_category: category }); } catch (e) {}
    try { if (typeof window.gtag === 'function') window.gtag('event', 'generate_lead', { method: category, content_name: name }); } catch (e) {}
    try { if (typeof window.clarity === 'function') window.clarity('event', 'lead_' + category); } catch (e) {}
  }

  /* ---------- hold-toggle: the smaller yes under a reserve button ----------
     One quiet line; tap it and the two fields unfold. Sits on the same root as
     joinlist, which does the sending. */
  register('hold-toggle', function (root) {
    var btn = root.querySelector('.hold__open');
    var form = root.querySelector('form');
    if (!btn || !form) return;
    btn.addEventListener('click', function () {
      var opening = form.hidden;
      form.hidden = !opening;
      btn.setAttribute('aria-expanded', opening ? 'true' : 'false');
      if (opening) { var f = form.querySelector('input'); if (f) setTimeout(function () { f.focus(); }, 40); }
    });
  });

  /* ---------- notify: sold-out email capture → Klaviyo ----------
     Root carries data-variant (the numeric Shopify variant id). Two roads,
     chosen by where the stock actually is (4 Sep 2026):

       1. Out of stock everywhere → Klaviyo back-in-stock subscription. Fires
          the Subscribed to Back in Stock metric, which the FX-01 flow waits
          on; Klaviyo releases it when any location restocks.
       2. Stocked somewhere, just not for this shopper's market (the AU shelf
          empty while the US warehouse is full, or the reverse) → the shopper
          joins the country waitlist instead, with the product saved on their
          profile. Klaviyo's back-in-stock check counts every location, so
          sending these people down road 1 released "It's back" emails to
          Australians for stock sitting in Pennsylvania.

     The store-wide count per variant and the market country come from
     window.FLIPSI_PDP, written by snippets/flipsi-tracking.liquid (rendered from
     layout/theme.liquid) on product pages. When
     that is missing (a non-product page, an old layout) the handler takes
     road 1, which is exactly what it did before this change.

     No optimistic success: a missing variant id or a failed request reads as
     an error, never as done. */
  var WAITLISTS = { AU: 'Wsg24g', US: 'RBn9N4' };   /* Klaviyo lists: FX | AU Waitlist, FX | US Waitlist */
  function stockScope(variant) {
    var pdp = window.FLIPSI_PDP || {};
    var qty = pdp.qty ? pdp.qty[String(variant)] : undefined;
    var country = String(pdp.country || (window.Shopify && window.Shopify.country) || '').toUpperCase();
    return {
      elsewhere: typeof qty === 'number' && qty > 0,
      country: country,
      list: WAITLISTS[country] || WAITLISTS.US,
      title: pdp.title || document.title
    };
  }
  register('notify', function (root) {
    var input = root.querySelector('input');
    var btn = root.querySelector('button');
    if (!input || !btn) return;
    var idleLabel = btn.textContent;
    function note(msg) {
      var m = root.querySelector('[data-fs-notify-msg]');
      if (!m) {
        m = document.createElement('p');
        m.setAttribute('data-fs-notify-msg', '');
        m.setAttribute('role', 'status');
        m.style.cssText = 'flex-basis:100%;width:100%;margin:8px 0 0;font-size:13px;';
        root.appendChild(m);
      }
      m.textContent = msg || '';
    }
    function submit() {
      var email = (input.value || '').trim();
      if (!emailOk(email)) { input.focus(); return; }
      var variant = root.dataset.variant;
      if (!variant) {
        if (window.console) console.error('[flipsi] notify: data-variant missing; not subscribing');
        note('That didn\'t work, please try again later.');
        return;
      }
      btn.disabled = true; btn.textContent = 'Adding...';
      var scope = stockScope(variant);
      var request;
      if (scope.elsewhere) {
        /* Road 2: stock exists in another market. Country waitlist, product on the profile. */
        var props = {};
        props['Waitlist product'] = scope.title;
        props['Waitlist variant'] = String(variant);
        props['Waitlist country'] = scope.country;
        props['Waitlist added'] = new Date().toISOString().slice(0, 10);
        request = klaviyoPost('subscriptions', {
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'pdp-notify-' + (scope.country || 'xx').toLowerCase(),
              profile: { data: { type: 'profile', attributes: { email: email, properties: props } } }
            },
            relationships: { list: { data: { type: 'list', id: scope.list } } }
          }
        });
      } else {
        /* Road 1: out everywhere. Klaviyo back in stock, feeds FX-01. */
        request = klaviyoPost('back-in-stock-subscriptions', {
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: { data: { type: 'profile', attributes: { email: email } } }
            },
            relationships: {
              variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variant } }
            }
          }
        });
      }
      request.then(function () {
        root.classList.add('is-sent');
        btn.disabled = true; btn.textContent = 'You are on the list';
        input.disabled = true;
        note('');
      }).catch(function (err) {
        if (window.console) console.error('[flipsi] notify', err);
        btn.disabled = false; btn.textContent = idleLabel;
        note('That didn\'t work, check the address and try again.');
      });
    }
    btn.addEventListener('click', submit);
    input.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); submit(); } });
  });

  /* ---------- joinlist: email capture → Klaviyo list subscribe ----------
     Root wraps a form[data-fs-notify] carrying data-klaviyo-list,
     data-source-tag, data-success and data-error (all section settings).
     Success only shows when Klaviyo accepts the request; an empty list ID
     reads as an error, never as done. */
  register('joinlist', function (root) {
    var form = root.querySelector('form[data-fs-notify]');
    if (!form) return;
    var email = form.querySelector('input[name="email"]');
    var first = form.querySelector('input[name="first_name"]');
    var btn = form.querySelector('button[type="submit"]');
    var msg = form.querySelector('[data-fs-joinmsg]');
    var idleLabel = btn ? btn.textContent : '';
    function say(text) {
      if (!msg) return;
      msg.hidden = !text;
      msg.textContent = text || '';
    }
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var addr = (email && email.value || '').trim();
      if (!emailOk(addr)) { if (email) email.focus(); return; }
      var listId = (form.dataset.klaviyoList || '').trim();
      if (!listId) {
        if (window.console) console.error('[flipsi] joinlist: data-klaviyo-list empty; not subscribing');
        say(form.dataset.error || 'That didn\'t work, please try again later.');
        return;
      }
      if (btn) { btn.disabled = true; btn.textContent = 'Adding...'; }
      var profileAttrs = { email: addr };
      if (first && first.value.trim()) { profileAttrs.first_name = first.value.trim(); }
      /* optional profile properties from the markup (which puzzle was held, its window) */
      if (form.dataset.fsProps) { try { profileAttrs.properties = JSON.parse(form.dataset.fsProps); } catch (e) {} }
      klaviyoPost('subscriptions', {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: form.dataset.sourceTag || 'homepage-joinlist',
            profile: { data: { type: 'profile', attributes: profileAttrs } }
          },
          relationships: { list: { data: { type: 'list', id: listId } } }
        }
      }).then(function () {
        form.classList.add('is-sent');
        if (email) email.disabled = true;
        if (first) first.disabled = true;
        if (btn) { btn.disabled = true; btn.textContent = idleLabel; }
        say(form.dataset.success || 'You are on the list.');
        if (form.dataset.fsTrack) {
          var held = profileAttrs.properties && profileAttrs.properties['Held puzzle'];
          fsLead(form.dataset.fsTrack, held || document.title);
        }
      }).catch(function (err) {
        if (window.console) console.error('[flipsi] joinlist', err);
        if (btn) { btn.disabled = false; btn.textContent = idleLabel; }
        say(form.dataset.error || 'That didn\'t work, check the address and try again.');
      });
    });
  });

  /* ---------- tier-toggle: single-PDP Set / Plus panel (source: tiers) ---------- */
  register('tier-toggle', function (root) {
    var tiers = data(root); if (!tiers) return;
    function q(sel) { return root.querySelector(sel); }
    function tier(k) {
      var d = tiers[k];
      var pre = document.body.classList.contains('fs-state-reserve');
      if (q('#tier-img')) q('#tier-img').src = d.img;
      if (q('#tier-kicker')) q('#tier-kicker').textContent = d.kicker;
      if (q('#tier-title')) q('#tier-title').textContent = d.title;
      if (q('#tier-inc')) q('#tier-inc').textContent = d.inc;
      if (q('#tier-price')) q('#tier-price').textContent = d.price;
      if (q('#tier-save')) q('#tier-save').textContent = d.save;
      if (q('#tier-cta')) q('#tier-cta').textContent = d.cta ? d.cta : (pre ? d.b : d.a);
      if (q('#tier-cta') && d.url) q('#tier-cta').setAttribute('href', d.url);
      if (q('#tier-note')) q('#tier-note').hidden = !d.note;
      if (q('#tier-flag')) q('#tier-flag').hidden = (k !== 'plus');
      if (q('#tier-panel')) q('#tier-panel').classList.toggle('is-plus', k === 'plus');
      if (q('#seg-set')) q('#seg-set').classList.toggle('is-on', k === 'set');
      if (q('#seg-plus')) q('#seg-plus').classList.toggle('is-on', k === 'plus');
    }
    var s = root.querySelector('#seg-set'), p = root.querySelector('#seg-plus');
    if (s) s.addEventListener('click', function () { tier('set'); });
    if (p) p.addEventListener('click', function () { tier('plus'); });
    /* no init call: the source markup's static initial state is canonical */
  });

  /* ---------- village-explorer: twin Naughty/Nice switch (source: efData) ---------- */
  register('village-explorer', function (root) {
    var efData = data(root); if (!efData) return;
    var blurb0 = root.querySelector('#c-blurb');
    if (blurb0 && efData[0].blurb) blurb0.textContent = efData[0].blurb;  /* source init */
    function setPuzzle(i) {
      var d = efData[i];
      root.classList.add('switching');
      setTimeout(function () {
        var img = root.querySelector('#art-img'); if (img) img.src = d.img;
        var cn = root.querySelector('#cap-name'); if (cn) cn.textContent = d.name;
        var n = root.querySelector('#c-name'); if (n) n.textContent = d.name;
        var su = root.querySelector('#c-sub'); if (su) su.textContent = d.sub;
        var bl = root.querySelector('#c-blurb'); if (bl && d.blurb) bl.textContent = d.blurb;
        root.classList.remove('switching');
      }, 220);
      root.querySelectorAll('[data-fs-seg] button').forEach(function (b, bi) { b.classList.toggle('on', bi === i); });
    }
    root.querySelectorAll('[data-fs-seg] button').forEach(function (b) {
      b.addEventListener('click', function () { setPuzzle(+b.dataset.i); });
    });
  });

  /* ---------- frame-picker: single / set-of-3 variant (source: variants) ---------- */
  register('frame-picker', function (root) {
    var variants = data(root); if (!variants) return;
    function pick(v) {
      var d = variants[v];
      var e;
      if ((e = root.querySelector('#price'))) e.textContent = d.price;
      if ((e = root.querySelector('#inc-1'))) e.innerHTML = d.inc1;
      if ((e = root.querySelector('#inc-2'))) e.innerHTML = d.inc2;
      if ((e = root.querySelector('#pre-line'))) e.textContent = d.line;
      if ((e = root.querySelector('[data-fs-frame-id]')) && d.variant) e.value = d.variant;
      root.querySelectorAll('[data-fs-var] button').forEach(function (b) { b.classList.toggle('on', b.dataset.v === v); });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    root.querySelectorAll('[data-fs-var] button').forEach(function (b) {
      b.addEventListener('click', function () { pick(b.dataset.v); });
    });
  });

  /* ---------- how-journey: arrow-scrolled journey track ---------- */
  register('journey', function (root) {
    var track = root.querySelector('[data-fs-jtrack]');
    if (!track) return;
    root.querySelectorAll('[data-fs-jdir]').forEach(function (b) {
      b.addEventListener('click', function () {
        track.scrollBy({ left: (+b.dataset.fsJdir) * 322, behavior: 'smooth' });
      });
    });
  });

  /* ---------- catalogue: shop-all filters + artwork toggle (cards are Liquid-rendered) ---------- */
  register('catalogue', function (root) {
    var chips = root.querySelector('[data-fs-chips]');
    var countEl = root.querySelector('[data-fs-count]');
    var total = root.querySelectorAll('.pcard').length;
    function applyFilter(f) {
      if (chips) chips.querySelectorAll('.chip').forEach(function (c) { c.classList.toggle('is-on', c.dataset.f === f); });
      var n = 0;
      root.querySelectorAll('.pcard').forEach(function (c) {
        var show = f === 'all' || c.dataset.type === f || (f === 'reserve' && c.dataset.state === 'reserve');
        c.classList.toggle('is-shown', show); if (show) n++;
      });
      root.querySelectorAll('.shelf').forEach(function (s) {
        s.style.display = s.querySelectorAll('.pcard.is-shown').length ? '' : 'none';
      });
      if (countEl) countEl.textContent = n + ' of ' + total + ' items';
    }
    if (chips) chips.addEventListener('click', function (e) {
      var b = e.target.closest('.chip'); if (b) applyFilter(b.dataset.f);
    });
    applyFilter('all');
    var mystery = document.querySelector('[data-fs-mystery]');
    if (mystery) {
      var singles = root.querySelectorAll('.pcard[data-type="single"]').length;
      mystery.innerHTML = singles + ' artworks on show. <em>' + singles + ' more in hiding.</em>';
    }
    var artbtn = root.querySelector('[data-fs-artbtn]') || document.querySelector('[data-fs-artbtn]');
    if (artbtn) artbtn.addEventListener('click', function () {
      var on = document.body.classList.toggle('show-art');
      artbtn.classList.toggle('is-on', on);
      artbtn.setAttribute('aria-pressed', on ? 'true' : 'false');
      artbtn.textContent = on ? 'Show the boxes' : 'Show the artwork';
    });
  });

  /* ---------- chooser: canonical bottom-sheet (locked decision; source logic 1:1) ---------- */
  register('chooser', function (root) {
    var D = data(root);
    if (!D) { if (window.console) console.error('[flipsi] chooser: data blob missing or invalid JSON'); return; }
    var CHW = D.CHW, CHQ = D.CHQ;
    var sheet = root.querySelector('[data-fs-sheet]');
    var mount = root.querySelector('[data-fs-mount]');
    /* the opener link can live in another section (e.g. worlds rail) — delegate at
       document level, once, so it also survives that section's own re-renders */
    if (!sheet || !mount) return;
    var CH = { step: 0, ans: {}, open: false };
    function chOpen() { CH.step = 0; CH.ans = {}; CH.open = true; sheet.classList.add('is-open'); document.body.classList.add('ch-lock'); render(); }
    function chClose() { CH.open = false; sheet.classList.remove('is-open'); document.body.classList.remove('ch-lock'); }
    function header(done) {
      var dots = '';
      for (var i = 0; i < CHQ.length; i++) { dots += '<i class="' + (done || i <= CH.step ? 'on' : '') + '"></i>'; }
      return '<div class="ch-head"><span class="ch-eyebrow">The 30-second chooser</span>' +
        '<div style="display:flex;align-items:center;gap:14px;"><div class="ch-dots">' + dots + '</div>' +
        '<button class="ch-close" aria-label="Close chooser" data-ch="close">&times;</button></div></div>';
    }
    function render() {
      if (CH.step >= CHQ.length) { return result(); }
      var q = CHQ[CH.step];
      var chips = q.opts.map(function (o) {
        return '<button class="ch-chip" data-ch="pick" data-k="' + q.key + '" data-v="' + o[0] + '">' + o[1] + '</button>';
      }).join('');
      mount.innerHTML = header(false) +
        '<div class="ch-q">' + (CH.step + 1) + ' — ' + q.q + '</div>' +
        '<div class="ch-chips">' + chips + '</div>' +
        (CH.step > 0 ? '<button class="ch-back" data-ch="back">&#8592; Back</button>' : '');
    }
    function badgeFor(av) {
      if (av === 'ship') return '<span class="badge badge--ship">Ships now</span>';
      if (av === 'reserve') return '<span class="badge badge--res">Reserve · next run</span>';
      return '<span class="badge badge--sold">Between runs</span>';
    }
    function result() {
      var a = CH.ans, notes = [];
      var mkey = (a.mood === 'surprise') ? 'spooky' : a.mood;
      if (a.mood === 'surprise') notes.push('You said surprise me — this is where most people start.');
      var w = CHW[mkey];
      var fmt = (a.format === 'notsure') ? 'plus' : a.format;
      if (a.format === 'notsure') notes.push('Not sure is a fine place to start — Plus is where most people do. If you’d rather dip a toe, a single tells you everything.');
      var av = w.avail;
      var title, price;
      if (w.twin) { title = 'Naughty | Nice Twin'; price = '$59 · twin pack'; }
      else {
        title = (fmt === 'one') ? w.single : (fmt === 'set') ? w.name + ' Set' : w.name + ' Flipsi Plus';
        price = (fmt === 'one') ? '$25' : (fmt === 'set') ? '$65 · saves $10' : '$89 · saves $15';
      }
      var img = w.img[fmt];
      var noteHtml = notes.length ? '<div class="ch-result__note">' + notes.join(' ') + '</div>' : '';
      var ctaLabel = (av === 'reserve') ? 'Reserve your copy' : (av === 'sold') ? 'Notify me' : 'View this puzzle';
      var soldLine = (av === 'sold') ? '<div class="ch-result__note">Between production runs — reserving reopens with the next one.</div>' : '';
      var resLine = (av === 'reserve') ? '<div class="ch-result__note">Held in the next production run — window shown before you commit. Cancel any time before final processing.</div>' : '';
      /* result markup 1:1 from the approved source (only inline onclick -> data-ch) */
      mount.innerHTML = header(true) +
        '<div class="ch-result">' +
        '<div class="ch-result__media"><img src="' + img + '" alt="' + title + '" onerror="imgFail(this)"></div>' +
        '<div>' +
        '<span class="ch-eyebrow">Your match</span>' +
        '<h3>' + title + '</h3>' +
        '<div class="ch-result__why">' + w.line + '</div>' +
        '<div class="ch-result__price">' + price + ' ' + badgeFor(av) + '</div>' +
        noteHtml + resLine + soldLine +
        '<div class="ch-result__ctas"><a class="btn btn--ink" style="height:46px;padding:0 24px;" href="#">' + ctaLabel + '</a>' +
        '<a href="#" style="font-size:11px;letter-spacing:.2em;text-transform:uppercase;font-weight:600;color:var(--gold);">Wander ' + w.name + ' &#8594;</a></div>' +
        '<div class="ch-result__alt"><a href="#" data-ch="again">&#8635; Start over</a> · or <a href="#worlds" data-ch="close">browse the worlds instead</a></div>' +
        '</div></div>';
    }
    mount.addEventListener('click', function (e) {
      var b = e.target.closest('[data-ch]'); if (!b) return;
      var act = b.dataset.ch;
      if (act === 'close') { chClose(); return; }  /* no preventDefault: #worlds link follows */
      e.preventDefault();
      if (act === 'back') { CH.step = Math.max(0, CH.step - 1); render(); }
      else if (act === 'again') { CH.step = 0; CH.ans = {}; render(); }
      else if (act === 'pick') { CH.ans[b.dataset.k] = b.dataset.v; CH.step++; render(); }
    });
    sheet.addEventListener('click', function (e) { if (e.target === sheet) chClose(); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && CH.open) chClose(); });
    root.__fsChooserOpen = chOpen; window.__fsChooserOpen = chOpen;   /* per-root + global opener */
    if (!document.body.dataset.fsChooserDelegate) {
      document.body.dataset.fsChooserDelegate = '1';
      document.addEventListener('click', function (e) {
        var op = e.target.closest('[data-fs-open]');
        if (!op) return;
        e.preventDefault();
        /* generic: find any live chooser root (survives multiple/re-rendered
           instances — the old flag-owner-closure pattern went stale after
           section re-renders and silently ate clicks) */
        var roots = document.querySelectorAll('[data-flipsi~="chooser"]');
        for (var i = 0; i < roots.length; i++) {
          if (roots[i].__fsChooserOpen) { roots[i].__fsChooserOpen(); return; }
        }
        if (window.console) console.warn('[flipsi] chooser trigger clicked but no initialized chooser found');
      }, true); /* capture phase: the theme editor's own click interception
                   (which stops propagation on preview links) runs later */
    }
  });

  /* ---------- global reveal scan: every .fade in flipsi modules (source had one page-wide observer) ---------- */
  var revIO = null;
  function revealScan(scope) {
    var els = (scope || document).querySelectorAll('.flipsi-module .fade:not([data-fs-rev]), [data-fsmod] .fade:not([data-fs-rev])');
    if (!els.length) return;
    if (EDITOR || REDUCED || !('IntersectionObserver' in window)) {
      for (var i = 0; i < els.length; i++) { els[i].classList.add('in'); els[i].setAttribute('data-fs-rev', '1'); }
      return;
    }
    if (!revIO) revIO = new IntersectionObserver(function (es) {
      es.forEach(function (e) { if (e.isIntersecting) { e.target.classList.add('in'); revIO.unobserve(e.target); } });
    }, { threshold: .15 });
    for (var k = 0; k < els.length; k++) { els[k].setAttribute('data-fs-rev', '1'); revIO.observe(els[k]); }
  }

  /* ---------- bootstrap ---------- */
  function boot() { initIn(document); revealScan(document); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', function (e) { initIn(e.target); revealScan(e.target); });
  document.addEventListener('shopify:block:select', function (e) {
    var d = e.target && e.target.closest && e.target.closest('details');
    if (d) d.open = true;
  });
})();
